import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
  cloneElement,
  isValidElement,
} from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../cn.js';

const control =
  'w-full rounded-md border border-line-strong bg-bg text-sm text-fg placeholder:text-fg-4 shadow-xs ' +
  'transition-[border-color,box-shadow] duration-100 ' +
  'hover:border-fg-4 focus:outline-none focus:border-brand focus:ring-3 focus:ring-brand/15 ' +
  'disabled:bg-subtle disabled:text-fg-3 disabled:cursor-not-allowed ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:ring-danger/15';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }>(
  function Input({ className, mono, ...props }, ref) {
    return <input ref={ref} className={cn(control, 'h-8 px-2.5', mono && 'font-mono text-xs', className)} {...props} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean }>(
  function Textarea({ className, mono, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(control, 'min-h-20 px-2.5 py-2 leading-relaxed', mono && 'font-mono text-xs', className)}
        {...props}
      />
    );
  },
);

export const NativeSelect = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function NativeSelect({ className, children, ...props }, ref) {
    return (
      <div className={cn('relative', className)}>
        <select ref={ref} className={cn(control, 'h-8 appearance-none pl-2.5 pr-8')} {...props}>
          {children}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-fg-3"
        />
      </div>
    );
  },
);

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  optional?: boolean;
  className?: string;
  children: ReactElement<Record<string, unknown>>;
}

/** Rótulo + controle + ajuda/erro, com associação ARIA automática. */
export function Field({ label, hint, error, optional, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const describedBy = error || hint ? hintId : undefined;
  const child = isValidElement(children)
    ? cloneElement(children, {
        id: (children.props.id as string | undefined) ?? id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
      })
    : children;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={(children.props.id as string | undefined) ?? id} className="text-xs font-medium text-fg-2">
        {label}
        {optional && <span className="font-normal text-fg-4"> · opcional</span>}
      </label>
      {child}
      {(error || hint) && (
        <p id={hintId} className={cn('text-xs', error ? 'text-danger' : 'text-fg-3')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

export function Switch({
  checked,
  onCheckedChange,
  disabled,
  label,
  id,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
  id?: string;
}) {
  return (
    <SwitchPrimitive.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors duration-100',
        'bg-line-strong data-[state=checked]:bg-brand disabled:opacity-50 disabled:cursor-not-allowed',
      )}
    >
      <SwitchPrimitive.Thumb className="block size-3 translate-x-0.5 rounded-full bg-white shadow-xs transition-transform duration-100 data-[state=checked]:translate-x-3.5" />
    </SwitchPrimitive.Root>
  );
}

export function Checkbox({
  checked,
  onCheckedChange,
  disabled,
  id,
  label,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  id?: string;
  label?: string;
}) {
  return (
    <CheckboxPrimitive.Root
      id={id}
      checked={checked}
      onCheckedChange={(v) => onCheckedChange(v === true)}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded-sm border border-line-strong bg-bg shadow-xs',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand disabled:opacity-50',
      )}
    >
      <CheckboxPrimitive.Indicator>
        <Check className="size-3 text-white" strokeWidth={3} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}
